import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Clock, Package, Scale, Ban, FileSpreadsheet, FileText, Columns3, X, Search, Filter,
  ArrowUp, ArrowDown, ArrowUpDown, RefreshCw, ChevronDown, ChevronUp, SlidersHorizontal
} from 'lucide-react';
import { API_URL, API_HOST } from '../../config/api';
import { exportSectionedReportToExcel } from '../../utils/reportExport';
import { MultiSelect } from './CustomReport';
import './CustomReport.css';
import './AgingInventory.css';

const COLS_STORAGE_KEY = 'agingReport.visibleColumns.v1';
const PAGE_SIZE = 100;

const METAL_OPTIONS = [
  { id: 'gold', name: 'Altın' },
  { id: 'silver', name: 'Gümüş' },
  { id: 'brands', name: 'Markalar' },
  { id: 'steel', name: 'Çelik' },
  { id: 'zippo', name: 'Zippo' },
  { id: 'aksesuar', name: 'Aksesuar' },
  { id: 'sarf', name: 'Sarf' },
];
const METAL_LABEL = Object.fromEntries(METAL_OPTIONS.map(m => [m.id, m.name]));

const BUCKETS = [
  { key: 'fresh', label: '0-29 Gün', min: 0, max: 29, color: '#0f766e', bg: '#ccfbf1' },
  { key: 'slow', label: '30-59 Gün', min: 30, max: 59, color: '#b45309', bg: '#fef3c7' },
  { key: 'warning', label: '60-89 Gün', min: 60, max: 89, color: '#c2410c', bg: '#ffedd5' },
  { key: 'critical', label: '90-179 Gün', min: 90, max: 179, color: '#dc2626', bg: '#fef2f2' },
  { key: 'dead', label: '180-364 Gün', min: 180, max: 364, color: '#991b1b', bg: '#fecaca' },
  { key: 'year', label: '365+ Gün', min: 365, max: Infinity, color: '#450a0a', bg: '#fca5a5' },
];
const bucketOf = (days) => BUCKETS.find(b => days >= b.min && days <= b.max) || BUCKETS[0];

const DAY_PRESETS = [30, 60, 90, 180, 365, 730];

const GROUP_OPTIONS = [
  { id: 'none', name: 'Gruplama Yok' },
  { id: 'bucket', name: 'Yaş Aralığı' },
  { id: 'category', name: 'Kategori' },
  { id: 'brand', name: 'Marka' },
  { id: 'supplier', name: 'Tedarikçi' },
  { id: 'metal', name: 'Ürün Türü' },
];
const groupKeyOf = (r, mode) => {
  switch (mode) {
    case 'bucket': return bucketOf(r.days_since_sale).label;
    case 'category': return r.category || 'Kategorisiz';
    case 'brand': return r.brand || 'Markasız';
    case 'supplier': return r.supplier || 'Tedarikçisiz';
    case 'metal': return METAL_LABEL[r.metal_type] || r.metal_type || 'Tanımsız';
    default: return '';
  }
};

const EMPTY_FILTERS = {
  // Satılmama kriteri
  minDays: '90', maxDays: '', notSoldSince: '', saleStatus: 'all', lastSaleAfter: '', lastSaleBefore: '',
  storeId: '', stockMode: 'in_stock', minStock: '', maxStock: '',
  // Ürün seçimi
  metalTypes: [], categories: [], brands: [], suppliers: [], search: '',
  // Ürün Yönetimi > Detaylı Filtre
  name: '', barcode: '', brand: '', supplier: '', description: '', notes: '', color: '', size: '', gender: '', createdBy: '',
  stockEntryAfter: '', stockEntryBefore: '', noStockChangeSince: '',
  createdAfter: '', createdBefore: '', updatedAfter: '', updatedBefore: '',
  minWeight: '', maxWeight: '', minLaborCost: '', maxLaborCost: '', minProfitMargin: '', maxProfitMargin: '',
  minMinStockThreshold: '', maxMinStockThreshold: '',
  goldKarat: '', silverPurity: '', currency: '', pricingType: '', shopifyStatus: '',
  hasImage: '', ecommerceActive: '', shopifyActive: '', trendyolActive: '', hepsiburadaActive: '', amazonActive: '', n11Active: '',
  isSet: '', isFavoriteFlag: '', eskiEtiket: '', noDiscount: '', isSpecialDefined: '',
};

const fmtDate = (d) => {
  if (!d) return '-';
  const x = new Date(d);
  return isNaN(x) ? '-' : x.toLocaleDateString('tr-TR');
};
const fmtDateTime = (d) => {
  if (!d) return '-';
  const x = new Date(d);
  return isNaN(x) ? '-' : `${x.toLocaleDateString('tr-TR')} ${x.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
};
const fmtNum = (n, digits = 2) => (n == null ? '-' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits }));
const fmtGram = (g) => (g == null || g === '' ? '-' : `${fmtNum(g, 2)} gr`);
const fmtInt = (n) => (Number(n) || 0).toLocaleString('tr-TR');
const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const karatText = (r) => {
  if (r.metal_type === 'gold' && r.gold_karat) return `${r.gold_karat} Ayar`;
  if (r.metal_type === 'silver' && r.silver_purity) return `${r.silver_purity}`;
  return '';
};

const buildColumns = (stores) => [
  { key: 'image', label: 'Görsel', def: true, noExcel: true, noSort: true },
  { key: 'barcode', label: 'Barkod', def: true, get: r => r.barcode || '' },
  { key: 'name', label: 'Ürün Adı', def: true, get: r => r.name || '' },
  { key: 'brand', label: 'Marka', def: true, get: r => r.brand || '' },
  { key: 'category', label: 'Kategori', def: true, get: r => r.category || '' },
  { key: 'supplier', label: 'Tedarikçi', def: false, get: r => r.supplier || '' },
  { key: 'metal_type', label: 'Ürün Türü', def: false, get: r => METAL_LABEL[r.metal_type] || r.metal_type || '' },
  { key: 'karat', label: 'Ayar', def: false, get: r => karatText(r) },
  { key: 'weight', label: 'Gramaj', def: true, num: true, get: r => (r.weight == null ? null : Number(r.weight)), fmt: r => fmtGram(r.weight) },
  { key: 'total_stock', label: 'Stok', def: true, num: true, get: r => r.total_stock, fmt: r => fmtInt(r.total_stock) },
  ...stores.map(s => ({
    key: `store_${s.id}`, label: `${s.name.replace(/^Akik\s+/i, '')} Stok`, def: false, num: true,
    get: r => r.store_stocks?.[s.id] || 0, fmt: r => fmtInt(r.store_stocks?.[s.id] || 0)
  })),
  { key: 'stock_gram', label: 'Stok Gramı', def: false, num: true, get: r => (Number(r.weight) || 0) * r.total_stock, fmt: r => (r.weight ? fmtGram((Number(r.weight) || 0) * r.total_stock) : '-') },
  { key: 'stock_entry_date', label: 'Stoğa Giriş', def: true, date: true, get: r => r.stock_entry_date, fmt: r => fmtDate(r.stock_entry_date) },
  { key: 'last_stock_change', label: 'Son Stok Hareketi', def: false, date: true, get: r => r.last_stock_change, fmt: r => fmtDate(r.last_stock_change) },
  { key: 'last_sale_date', label: 'Son Satış', def: true, date: true, get: r => r.last_sale_date, fmt: r => (r.last_sale_date ? fmtDate(r.last_sale_date) : 'Hiç satılmadı') },
  { key: 'days_since_sale', label: 'Satılmayan Gün', def: true, num: true, get: r => r.days_since_sale, fmt: r => fmtInt(r.days_since_sale) },
  { key: 'bucket', label: 'Durum', def: true, noSort: true, get: r => bucketOf(r.days_since_sale).label },
  { key: 'sold_qty', label: 'Toplam Satılan', def: false, num: true, get: r => r.sold_qty, fmt: r => fmtInt(r.sold_qty) },
  { key: 'sale_count', label: 'Satış Fişi', def: false, num: true, get: r => r.sale_count, fmt: r => fmtInt(r.sale_count) },
  { key: 'product_created_at', label: 'Oluşturma', def: false, date: true, get: r => r.product_created_at, fmt: r => fmtDate(r.product_created_at) },
  { key: 'created_by', label: 'Oluşturan', def: false, get: r => r.created_by || '' },
];

const summarize = (list) => {
  const t = { count: 0, stock: 0, gram: 0, never: 0, daysSum: 0 };
  for (const r of list) {
    t.count += 1;
    t.stock += r.total_stock;
    t.gram += (Number(r.weight) || 0) * r.total_stock;
    if (!r.last_sale_date) t.never += 1;
    t.daysSum += r.days_since_sale;
  }
  t.avgDays = t.count ? Math.round(t.daysSum / t.count) : 0;
  return t;
};

// Küçük form yardımcıları
const Field = ({ label, children }) => (
  <div className="ai-field">
    <label className="cr-field-label">{label}</label>
    {children}
  </div>
);
const YesNo = ({ label, value, onChange, yes = 'Evet', no = 'Hayır' }) => (
  <Field label={label}>
    <select className="ai-input" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">Tümü</option>
      <option value="yes">{yes}</option>
      <option value="no">{no}</option>
    </select>
  </Field>
);

const AgingInventory = () => {
  const [options, setOptions] = useState({ categories: [], brands: [], suppliers: [], stores: [] });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [showDetailed, setShowDetailed] = useState(false);

  const [items, setItems] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeBucket, setActiveBucket] = useState(null);
  const [groupBy, setGroupBy] = useState('category');
  const [groupSort, setGroupSort] = useState({ field: 'stock', dir: 'desc' });
  const [groupFocus, setGroupFocus] = useState(null);
  const [sort, setSort] = useState({ field: 'days_since_sale', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [colsOpen, setColsOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const colsRef = useRef(null);

  const COLUMNS = useMemo(() => buildColumns(options.stores || []), [options.stores]);

  const [visible, setVisible] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLS_STORAGE_KEY) || 'null');
      if (saved && typeof saved === 'object') return saved;
    } catch (_) { /* yok say */ }
    return {};
  });
  const isVisible = (c) => (visible[c.key] !== undefined ? !!visible[c.key] : c.def);
  useEffect(() => {
    try { localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(visible)); } catch (_) { /* yok say */ }
  }, [visible]);
  useEffect(() => {
    if (!colsOpen) return;
    const h = (e) => { if (colsRef.current && !colsRef.current.contains(e.target)) setColsOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [colsOpen]);

  useEffect(() => {
    fetch(`${API_URL}/reports/aging-inventory/options`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setOptions(d); })
      .catch(e => console.error(e));
  }, []);

  // Yazarken her tuşta sorgu atmasın
  useEffect(() => {
    const t = setTimeout(() => setApplied(filters), 500);
    return () => clearTimeout(t);
  }, [filters]);

  const set = (key) => (value) => setFilters(f => ({ ...f, [key]: value }));
  const bind = (key) => ({ value: filters[key], onChange: (e) => set(key)(e.target.value) });

  const reqIdRef = useRef(0);
  const fetchData = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const { storeId, ...rest } = applied;
      const res = await fetch(`${API_URL}/reports/aging-inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...rest, store_id: storeId || null })
      });
      const data = await res.json();
      if (reqId !== reqIdRef.current) return;
      if (!res.ok) throw new Error(data?.error || 'Rapor alınamadı');
      setItems(data.items || []);
      setTruncated(!!data.truncated);
      setPage(1);
      setGroupFocus(null);
      setActiveBucket(null);
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      console.error(e);
      setError(e.message);
      setItems([]);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [applied]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeColumns = COLUMNS.filter(isVisible);

  // Kova (yaş aralığı) kartları — sunucudan gelen tüm sonuç üzerinden
  const bucketData = useMemo(() => BUCKETS.map(b => {
    const list = items.filter(r => r.days_since_sale >= b.min && r.days_since_sale <= b.max);
    return { ...b, ...summarize(list) };
  }), [items]);

  const bucketItems = useMemo(
    () => (activeBucket ? items.filter(r => bucketOf(r.days_since_sale).key === activeBucket) : items),
    [items, activeBucket]
  );

  const groups = useMemo(() => {
    if (groupBy === 'none') return [];
    const map = new Map();
    for (const r of bucketItems) {
      const k = groupKeyOf(r, groupBy);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    const totalStock = bucketItems.reduce((s, r) => s + r.total_stock, 0);
    const arr = [...map.entries()].map(([key, list]) => {
      const s = summarize(list);
      return { key, ...s, share: totalStock ? (s.stock / totalStock) * 100 : 0 };
    });
    const { field, dir } = groupSort;
    const m = dir === 'asc' ? 1 : -1;
    if (groupBy === 'bucket' && field === 'key') {
      const order = Object.fromEntries(BUCKETS.map((b, i) => [b.label, i]));
      arr.sort((a, b) => m * (order[a.key] - order[b.key]));
    } else {
      arr.sort((a, b) => {
        if (field === 'key') return m * String(a.key).localeCompare(String(b.key), 'tr', { numeric: true });
        return m * ((a[field] || 0) - (b[field] || 0));
      });
    }
    return arr;
  }, [bucketItems, groupBy, groupSort]);

  const detailRows = useMemo(() => {
    let list = groupFocus != null ? bucketItems.filter(r => groupKeyOf(r, groupBy) === groupFocus) : bucketItems;
    const col = COLUMNS.find(c => c.key === sort.field);
    if (col && col.get) {
      const m = sort.dir === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => {
        let va = col.get(a), vb = col.get(b);
        if (col.date) { va = va ? new Date(va).getTime() : 0; vb = vb ? new Date(vb).getTime() : 0; }
        if (col.num || col.date) return m * ((Number(va) || 0) - (Number(vb) || 0));
        return m * String(va).localeCompare(String(vb), 'tr', { numeric: true });
      });
    }
    return list;
  }, [bucketItems, groupFocus, groupBy, sort, COLUMNS]);

  const totals = useMemo(() => summarize(items), [items]);
  const detailTotals = useMemo(() => summarize(detailRows), [detailRows]);
  const pageCount = Math.max(1, Math.ceil(detailRows.length / PAGE_SIZE));
  const pageRows = detailRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (field) => {
    setSort(s => (s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' }));
    setPage(1);
  };
  const toggleGroupSort = (field) => {
    setGroupSort(s => (s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: field === 'key' ? 'asc' : 'desc' }));
  };
  const SortIcon = ({ active, dir }) => (active ? (dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="cr-sort-idle" />);

  const totalCell = (key, t) => {
    switch (key) {
      case 'total_stock': return fmtInt(t.stock);
      case 'stock_gram': return fmtGram(t.gram);
      case 'days_since_sale': return `ort. ${fmtInt(t.avgDays)}`;
      default: return '';
    }
  };

  // ---- Filtre özeti ----
  const differs = (k) => {
    const v = filters[k], d = EMPTY_FILTERS[k];
    return Array.isArray(v) ? v.length > 0 : v !== d;
  };
  const MAIN_KEYS = new Set(['minDays', 'maxDays', 'notSoldSince', 'saleStatus', 'storeId', 'stockMode', 'minStock', 'maxStock', 'metalTypes', 'categories', 'brands', 'suppliers', 'search']);
  const detailedActiveCount = Object.keys(EMPTY_FILTERS).filter(k => !MAIN_KEYS.has(k) && differs(k)).length;
  const activeFilterCount = Object.keys(EMPTY_FILTERS).filter(differs).length;

  const storeName = (id) => options.stores?.find(s => String(s.id) === String(id))?.name || `Mağaza #${id}`;
  const storeLabel = applied.storeId ? storeName(applied.storeId) : 'Tüm Mağazalar';

  const LABELS = {
    name: 'Ürün Adı', barcode: 'Barkod', brand: 'Marka (metin)', supplier: 'Tedarikçi (metin)', description: 'Açıklama', notes: 'Notlar',
    color: 'Renk', size: 'Beden', gender: 'Cinsiyet', createdBy: 'Oluşturan',
    stockEntryAfter: 'Stoğa giriş ≥', stockEntryBefore: 'Stoğa giriş ≤', noStockChangeSince: 'Şu tarihten beri stok hareketi yok',
    createdAfter: 'Oluşturma ≥', createdBefore: 'Oluşturma ≤', updatedAfter: 'Güncelleme ≥', updatedBefore: 'Güncelleme ≤',
    minWeight: 'Min gramaj', maxWeight: 'Max gramaj', minLaborCost: 'Min işçilik', maxLaborCost: 'Max işçilik',
    minProfitMargin: 'Min kâr marjı', maxProfitMargin: 'Max kâr marjı', minMinStockThreshold: 'Min stok eşiği', maxMinStockThreshold: 'Max stok eşiği',
    goldKarat: 'Altın ayarı', silverPurity: 'Gümüş saflığı', currency: 'Para birimi', pricingType: 'Fiyatlama', shopifyStatus: 'Shopify durumu',
    hasImage: 'Fotoğraf', ecommerceActive: 'E-Ticaret', shopifyActive: 'Shopify', trendyolActive: 'Trendyol', hepsiburadaActive: 'Hepsiburada',
    amazonActive: 'Amazon', n11Active: 'N11', isSet: 'Set ürün', isFavoriteFlag: 'Favori', eskiEtiket: 'Eski etiket',
    noDiscount: 'İndirimsiz', isSpecialDefined: 'Özel tanımlı', minStock: 'Min stok', maxStock: 'Max stok',
    lastSaleAfter: 'Son satış ≥', lastSaleBefore: 'Son satış ≤',
  };
  const criteriaText = (f = applied) => {
    const parts = [];
    if (f.minDays !== '' && f.maxDays !== '') parts.push(`${f.minDays}-${f.maxDays} gündür satılmayan`);
    else if (f.minDays !== '') parts.push(`En az ${f.minDays} gündür satılmayan`);
    else if (f.maxDays !== '') parts.push(`En fazla ${f.maxDays} gündür satılmayan`);
    if (f.notSoldSince) parts.push(`${fmtDate(f.notSoldSince)} tarihinden beri satılmayan`);
    if (f.saleStatus === 'never') parts.push('Hiç satılmamış');
    if (f.saleStatus === 'sold') parts.push('En az bir kez satılmış');
    return parts.join(', ') || 'Tüm ürünler';
  };
  const filterSummaryText = () => {
    const f = applied;
    const parts = [criteriaText(f)];
    parts.push(`Mağaza: ${storeLabel}`);
    parts.push(f.stockMode === 'in_stock' ? 'Stoktakiler' : f.stockMode === 'out_of_stock' ? 'Stoğu bitenler' : 'Stok durumu: tümü');
    if (f.metalTypes.length) parts.push(`Tür: ${f.metalTypes.map(m => METAL_LABEL[m] || m).join(', ')}`);
    if (f.categories.length) parts.push(`Kategori: ${f.categories.join(', ')}`);
    if (f.brands.length) parts.push(`Marka: ${f.brands.join(', ')}`);
    if (f.suppliers.length) parts.push(`Tedarikçi: ${f.suppliers.join(', ')}`);
    if (f.search.trim()) parts.push(`Arama: "${f.search.trim()}"`);
    Object.keys(LABELS).forEach(k => {
      const v = f[k];
      if (v === '' || v == null) return;
      const shown = v === 'yes' ? 'Evet' : v === 'no' ? 'Hayır' : /^\d{4}-\d{2}-\d{2}$/.test(v) ? fmtDate(v) : v;
      parts.push(`${LABELS[k]}: ${shown}`);
    });
    if (activeBucket) parts.push(`Yaş aralığı: ${BUCKETS.find(b => b.key === activeBucket)?.label}`);
    if (groupFocus != null) parts.push(`Grup: ${groupFocus}`);
    return parts.join(' | ');
  };

  const groupLabel = GROUP_OPTIONS.find(g => g.id === groupBy)?.name || '';

  // ---- EXCEL ----
  const exportExcel = () => {
    const cols = activeColumns.filter(c => !c.noExcel);
    const excelVal = (c, r) => {
      if (c.key === 'last_sale_date') return r.last_sale_date ? fmtDate(r.last_sale_date) : 'Hiç satılmadı';
      if (c.date) return c.fmt(r);
      if (c.key === 'stock_gram') return r.weight ? Number(((Number(r.weight) || 0) * r.total_stock).toFixed(3)) : '';
      if (c.num) { const v = c.get(r); return v == null ? '' : Number(v); }
      return c.get(r);
    };
    const totalRow = cols.map((c, i) => {
      if (i === 0) return 'TOPLAM';
      if (c.key === 'total_stock') return detailTotals.stock;
      if (c.key === 'stock_gram') return Number(detailTotals.gram.toFixed(3));
      if (c.key === 'days_since_sale') return detailTotals.avgDays;
      if (c.key.startsWith('store_')) return detailRows.reduce((s, r) => s + (c.get(r) || 0), 0);
      return '';
    });
    const sections = [];
    sections.push({
      heading: 'Yaş Aralığı Özeti',
      columns: ['Yaş Aralığı', 'Ürün', 'Stok Adedi', 'Stok Gramı', 'Hiç Satılmamış'],
      colWidths: [{ wch: 18 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 16 }],
      rows: [
        ...bucketData.map(b => [b.label, b.count, b.stock, Number(b.gram.toFixed(3)), b.never]),
        { kind: 'total', span: 1, cells: ['TOPLAM', totals.count, totals.stock, Number(totals.gram.toFixed(3)), totals.never] }
      ]
    });
    if (groupBy !== 'none' && groupBy !== 'bucket' && groups.length) {
      const t = summarize(bucketItems);
      sections.push({
        heading: `${groupLabel} Özeti`,
        columns: [groupLabel, 'Ürün', 'Stok Adedi', 'Stok Gramı', 'Hiç Satılmamış', 'Ort. Gün', 'Stok Payı %'],
        colWidths: [{ wch: 36 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 10 }, { wch: 12 }],
        rows: [
          ...groups.map(g => [g.key, g.count, g.stock, Number(g.gram.toFixed(3)), g.never, g.avgDays, Number(g.share.toFixed(2))]),
          { kind: 'total', span: 1, cells: ['TOPLAM', t.count, t.stock, Number(t.gram.toFixed(3)), t.never, t.avgDays, 100] }
        ]
      });
    }
    sections.push({
      heading: 'Ürün Detayı',
      columns: cols.map(c => c.label),
      colWidths: cols.map(c => ({ wch: c.key === 'name' ? 38 : c.key === 'barcode' ? 16 : c.date ? 14 : 12 })),
      rows: [
        ...detailRows.map(r => cols.map(c => excelVal(c, r))),
        { kind: 'total', span: 1, cells: totalRow }
      ]
    });
    sections.push({
      heading: 'Filtreler',
      columns: ['Filtre'],
      colWidths: [{ wch: 120 }],
      rows: filterSummaryText().split(' | ').map(s => [s])
    });
    exportSectionedReportToExcel({
      title: 'Yaşlanan Stok Raporu',
      periodLabel: criteriaText(),
      storeLabel,
      sections
    });
  };

  // ---- PDF (yazdır → PDF olarak kaydet) ----
  const exportPdf = () => {
    const cols = activeColumns;
    const withImages = cols.some(c => c.key === 'image');
    if (withImages && detailRows.length > 1500 &&
      !window.confirm(`${fmtInt(detailRows.length)} ürün görselleriyle PDF'e aktarılacak; bu uzun sürebilir. Devam edilsin mi?\n(Hızlı çıktı için "Sütunlar"dan Görsel'i kapatabilirsiniz.)`)) return;
    const w = window.open('', '_blank');
    if (!w) { alert('Açılır pencere engellendi. Lütfen tarayıcıda izin verin.'); return; }
    const cell = (c, r) => {
      if (c.key === 'image') return r.image ? `<img src="${escapeHtml(API_HOST + r.image)}" alt="" />` : '';
      if (c.key === 'bucket') {
        const b = bucketOf(r.days_since_sale);
        return `<span class="bdg" style="background:${b.bg};color:${b.color}">${escapeHtml(b.label)}</span>`;
      }
      const v = c.fmt ? c.fmt(r) : c.get(r);
      return escapeHtml(v === '' || v == null ? '-' : v);
    };
    const t = summarize(bucketItems);
    const groupHtml = (groupBy !== 'none' && groups.length && groupFocus == null) ? `
      <h2>${escapeHtml(groupLabel)} Özeti</h2>
      <table>
        <thead><tr><th>${escapeHtml(groupLabel)}</th><th class="r">Ürün</th><th class="r">Stok Adedi</th><th class="r">Stok Gramı</th><th class="r">Hiç Satılmamış</th><th class="r">Ort. Gün</th><th class="r">Stok Payı</th></tr></thead>
        <tbody>
          ${groups.map(g => `<tr><td>${escapeHtml(g.key)}</td><td class="r">${fmtInt(g.count)}</td><td class="r"><b>${fmtInt(g.stock)}</b></td><td class="r">${fmtGram(g.gram)}</td><td class="r">${fmtInt(g.never)}</td><td class="r">${fmtInt(g.avgDays)}</td><td class="r">%${fmtNum(g.share)}</td></tr>`).join('')}
          <tr class="tot"><td>TOPLAM</td><td class="r">${fmtInt(t.count)}</td><td class="r">${fmtInt(t.stock)}</td><td class="r">${fmtGram(t.gram)}</td><td class="r">${fmtInt(t.never)}</td><td class="r">${fmtInt(t.avgDays)}</td><td class="r">%100</td></tr>
        </tbody>
      </table>` : '';
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Yaşlanan Stok Raporu - ${escapeHtml(fmtDate(new Date()))}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; color: #1e293b; margin: 18px; font-size: 10px; }
        .hdr { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #0f766e; padding-bottom: 8px; margin-bottom: 10px; }
        .hdr h1 { margin: 0; font-size: 20px; color: #0f766e; }
        .meta { font-size: 10px; color: #475569; line-height: 1.5; text-align: right; }
        .filters { background: #f1f5f9; border-left: 3px solid #0f766e; padding: 6px 10px; margin-bottom: 10px; font-size: 9.5px; color: #334155; }
        .kpis { display: flex; gap: 8px; margin-bottom: 12px; }
        .kpi { flex: 1; border: 1px solid #cbd5e1; border-radius: 6px; padding: 6px 8px; }
        .kpi b { display: block; font-size: 13px; color: #0f766e; }
        .kpi span { font-size: 9px; color: #64748b; }
        h2 { font-size: 13px; margin: 14px 0 6px; color: #334155; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #0f766e; color: #fff; padding: 5px 4px; font-size: 9px; text-align: left; }
        td { padding: 4px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; }
        tr:nth-child(even) td { background: #f8fafc; }
        .r { text-align: right; white-space: nowrap; }
        .tot td { background: #ccfbf1 !important; font-weight: 700; border-top: 2px solid #0f766e; }
        .bdg { border-radius: 4px; padding: 1px 5px; font-weight: 600; white-space: nowrap; }
        img { width: 36px; height: 36px; object-fit: cover; border-radius: 4px; display: block; }
        thead { display: table-header-group; }
        tr { page-break-inside: avoid; }
        @page { size: A4 landscape; margin: 10mm; }
      </style></head><body>
      <div class="hdr"><h1>AKİK · Yaşlanan Stok Raporu</h1>
        <div class="meta">Kriter: <b>${escapeHtml(criteriaText())}</b><br/>Mağaza: <b>${escapeHtml(storeLabel)}</b><br/>Oluşturulma: ${escapeHtml(fmtDateTime(new Date()))}</div>
      </div>
      <div class="filters">${escapeHtml(filterSummaryText())}</div>
      <div class="kpis">
        <div class="kpi"><b>${fmtInt(detailTotals.count)}</b><span>Ürün</span></div>
        <div class="kpi"><b>${fmtInt(detailTotals.stock)}</b><span>Stok Adedi</span></div>
        <div class="kpi"><b>${fmtGram(detailTotals.gram)}</b><span>Stok Gramı</span></div>
        <div class="kpi"><b>${fmtInt(detailTotals.never)}</b><span>Hiç Satılmamış</span></div>
        <div class="kpi"><b>${fmtInt(detailTotals.avgDays)} gün</b><span>Ort. Satılmama Süresi</span></div>
      </div>
      ${groupHtml}
      <h2>Ürün Detayı (${fmtInt(detailRows.length)} ürün)</h2>
      <table>
        <thead><tr>${cols.map(c => `<th class="${c.num ? 'r' : ''}">${escapeHtml(c.label)}</th>`).join('')}</tr></thead>
        <tbody>
          ${detailRows.map(r => `<tr>${cols.map(c => `<td class="${c.num ? 'r' : ''}">${cell(c, r)}</td>`).join('')}</tr>`).join('')}
          <tr class="tot">${cols.map((c, i) => `<td class="${c.num ? 'r' : ''}">${i === 0 ? 'TOPLAM' : escapeHtml(c.key.startsWith('store_') ? fmtInt(detailRows.reduce((s, r) => s + (c.get(r) || 0), 0)) : totalCell(c.key, detailTotals))}</td>`).join('')}</tr>
        </tbody>
      </table>
      <script>
        (function () {
          var imgs = Array.prototype.slice.call(document.images);
          var left = imgs.length, done = false;
          function go() { if (done) return; done = true; setTimeout(function () { window.print(); }, 150); }
          if (!left) return go();
          imgs.forEach(function (im) {
            if (im.complete) { if (--left === 0) go(); }
            else { im.onload = im.onerror = function () { if (--left === 0) go(); }; }
          });
          setTimeout(go, 20000);
        })();
      </script>
      </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const clearFilters = () => { setFilters(EMPTY_FILTERS); };
  const presetDays = (d) => setFilters(f => ({ ...f, minDays: f.minDays === String(d) ? '' : String(d), maxDays: '', notSoldSince: '' }));

  return (
    <div className="tab-content cr-root ai-root">
      {/* SATILMAMA KRİTERİ + ANA FİLTRELER */}
      <div className="data-card cr-filters">
        <div className="cr-filters-head">
          <h3><Filter size={16} /> Filtreler {activeFilterCount > 0 && <span className="cr-badge">{activeFilterCount}</span>}</h3>
          <div className="cr-filters-head-actions">
            {activeFilterCount > 0 && <button className="cr-link-btn" onClick={clearFilters}>Filtreleri sıfırla</button>}
            <button className="cr-icon-btn" onClick={fetchData} title="Yenile" disabled={loading}><RefreshCw size={15} className={loading ? 'cr-spin' : ''} /></button>
          </div>
        </div>
        <p className="cr-hint">Yukarıdaki dönem seçimi bu raporu etkilemez. Satılmama süresi son satıştan, hiç satılmamışsa stoğa giriş tarihinden itibaren sayılır; iadeler satış sayılmaz.</p>

        <div className="ai-criteria">
          <div className="ai-criteria-row">
            <label className="cr-field-label"><Clock size={13} /> En az şu kadar gündür satılmayan</label>
            <div className="cr-chips">
              {DAY_PRESETS.map(d => (
                <button key={d} type="button" className={`cr-chip ${filters.minDays === String(d) && !filters.maxDays ? 'active' : ''}`} onClick={() => presetDays(d)}>
                  {d >= 365 ? `${d / 365} yıl` : `${d} gün`}
                </button>
              ))}
            </div>
          </div>
          <div className="ai-grid">
            <Field label="Min. Gün">
              <input className="ai-input" type="number" min="0" placeholder="örn. 90" {...bind('minDays')} />
            </Field>
            <Field label="Max. Gün">
              <input className="ai-input" type="number" min="0" placeholder="sınırsız" {...bind('maxDays')} />
            </Field>
            <Field label="Şu tarihten beri satılmayan">
              <div className="ai-date-wrap">
                <input className="ai-input" type="date" max={isoDaysAgo(0)} {...bind('notSoldSince')} />
                {filters.notSoldSince && <X size={14} className="ai-date-clear" onClick={() => set('notSoldSince')('')} />}
              </div>
            </Field>
            <Field label="Satış Geçmişi">
              <select className="ai-input" {...bind('saleStatus')}>
                <option value="all">Tümü</option>
                <option value="never">Hiç satılmamış</option>
                <option value="sold">En az bir kez satılmış</option>
              </select>
            </Field>
            <Field label="Mağaza (stok + satış)">
              <select className="ai-input" {...bind('storeId')}>
                <option value="">Tüm Mağazalar</option>
                {(options.stores || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Stok Durumu">
              <select className="ai-input" {...bind('stockMode')}>
                <option value="in_stock">Stokta olanlar</option>
                <option value="out_of_stock">Stoğu bitenler</option>
                <option value="all">Tümü</option>
              </select>
            </Field>
            <Field label="Min. Stok">
              <input className="ai-input" type="number" placeholder="Min" {...bind('minStock')} />
            </Field>
            <Field label="Max. Stok">
              <input className="ai-input" type="number" placeholder="Max" {...bind('maxStock')} />
            </Field>
          </div>
        </div>

        <div className="cr-metal-row">
          <label className="cr-field-label">Ürün Türü</label>
          <div className="cr-chips">
            {METAL_OPTIONS.map(m => (
              <button key={m.id} type="button"
                className={`cr-chip ${filters.metalTypes.includes(m.id) ? 'active' : ''}`}
                onClick={() => set('metalTypes')(filters.metalTypes.includes(m.id) ? filters.metalTypes.filter(x => x !== m.id) : [...filters.metalTypes, m.id])}>
                {m.name}
              </button>
            ))}
          </div>
        </div>

        <div className="cr-filter-grid">
          <MultiSelect label="Kategori" options={options.categories || []} selected={filters.categories} onChange={set('categories')} />
          <MultiSelect label="Marka" options={options.brands || []} selected={filters.brands} onChange={set('brands')} />
          <MultiSelect label="Tedarikçi" options={options.suppliers || []} selected={filters.suppliers} onChange={set('suppliers')} />
          <div className="cr-field">
            <label className="cr-field-label">Ürün Ara</label>
            <div className="cr-search">
              <Search size={14} />
              <input value={filters.search} onChange={e => set('search')(e.target.value)} placeholder="Ürün adı, barkod veya SKU" />
              {filters.search && <X size={14} className="cr-search-clear" onClick={() => set('search')('')} />}
            </div>
          </div>
        </div>

        {/* DETAYLI FİLTRE (Ürün Yönetimi ile aynı alanlar) */}
        <button type="button" className="ai-toggle" onClick={() => setShowDetailed(s => !s)}>
          <SlidersHorizontal size={15} /> Detaylı Filtre (Ürün Yönetimi)
          {detailedActiveCount > 0 && <span className="cr-badge">{detailedActiveCount}</span>}
          {showDetailed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {showDetailed && (
          <div className="ai-detailed">
            <div className="ai-section">
              <h4>Metin</h4>
              <div className="ai-grid">
                <Field label="Ürün Adı"><input className="ai-input" placeholder="Ürün adı ara..." {...bind('name')} /></Field>
                <Field label="Barkod"><input className="ai-input" placeholder="Barkod ara..." {...bind('barcode')} /></Field>
                <Field label="Marka"><input className="ai-input" placeholder="Marka ara..." {...bind('brand')} /></Field>
                <Field label="Tedarikçi"><input className="ai-input" placeholder="Tedarikçi ara..." {...bind('supplier')} /></Field>
                <Field label="Açıklama"><input className="ai-input" placeholder="Açıklama ara..." {...bind('description')} /></Field>
                <Field label="Notlar"><input className="ai-input" placeholder="Not ara..." {...bind('notes')} /></Field>
                <Field label="Renk"><input className="ai-input" placeholder="Renk" {...bind('color')} /></Field>
                <Field label="Beden"><input className="ai-input" placeholder="Beden" {...bind('size')} /></Field>
                <Field label="Cinsiyet">
                  <select className="ai-input" {...bind('gender')}>
                    <option value="">Tümü</option>
                    <option value="kadın">Kadın</option>
                    <option value="erkek">Erkek</option>
                    <option value="unisex">Unisex</option>
                    <option value="çocuk">Çocuk</option>
                  </select>
                </Field>
                <Field label="Oluşturan"><input className="ai-input" placeholder="Kullanıcı adı" {...bind('createdBy')} /></Field>
              </div>
            </div>

            <div className="ai-section">
              <h4>Tarihler</h4>
              <div className="ai-grid">
                <Field label="Stoğa Giriş (Sonra)"><input className="ai-input" type="date" {...bind('stockEntryAfter')} /></Field>
                <Field label="Stoğa Giriş (Önce)"><input className="ai-input" type="date" {...bind('stockEntryBefore')} /></Field>
                <Field label="Stok Hareketi Olmayan (şu tarihten beri)"><input className="ai-input" type="date" {...bind('noStockChangeSince')} /></Field>
                <Field label="Son Satış (Sonra)"><input className="ai-input" type="date" {...bind('lastSaleAfter')} /></Field>
                <Field label="Son Satış (Önce)"><input className="ai-input" type="date" {...bind('lastSaleBefore')} /></Field>
                <Field label="Oluşturma (Sonra)"><input className="ai-input" type="date" {...bind('createdAfter')} /></Field>
                <Field label="Oluşturma (Önce)"><input className="ai-input" type="date" {...bind('createdBefore')} /></Field>
                <Field label="Son Güncelleme (Sonra)"><input className="ai-input" type="date" {...bind('updatedAfter')} /></Field>
                <Field label="Son Güncelleme (Önce)"><input className="ai-input" type="date" {...bind('updatedBefore')} /></Field>
              </div>
            </div>

            <div className="ai-section">
              <h4>Sayısal</h4>
              <div className="ai-grid">
                <Field label="Min. Ağırlık (gr)"><input className="ai-input" type="number" step="0.01" {...bind('minWeight')} /></Field>
                <Field label="Max. Ağırlık (gr)"><input className="ai-input" type="number" step="0.01" {...bind('maxWeight')} /></Field>
                <Field label="Min. İşçilik"><input className="ai-input" type="number" step="0.01" {...bind('minLaborCost')} /></Field>
                <Field label="Max. İşçilik"><input className="ai-input" type="number" step="0.01" {...bind('maxLaborCost')} /></Field>
                <Field label="Min. Kâr Marjı (x)"><input className="ai-input" type="number" step="0.01" {...bind('minProfitMargin')} /></Field>
                <Field label="Max. Kâr Marjı (x)"><input className="ai-input" type="number" step="0.01" {...bind('maxProfitMargin')} /></Field>
                <Field label="Min. Stok Eşiği"><input className="ai-input" type="number" {...bind('minMinStockThreshold')} /></Field>
                <Field label="Max. Stok Eşiği"><input className="ai-input" type="number" {...bind('maxMinStockThreshold')} /></Field>
              </div>
            </div>

            <div className="ai-section">
              <h4>Özellikler</h4>
              <div className="ai-grid">
                <Field label="Altın Ayarı">
                  <select className="ai-input" {...bind('goldKarat')}>
                    <option value="">Tümü</option>
                    {['8', '14', '18', '22', '24'].map(k => <option key={k} value={k}>{k} ayar</option>)}
                  </select>
                </Field>
                <Field label="Gümüş Saflığı">
                  <select className="ai-input" {...bind('silverPurity')}>
                    <option value="">Tümü</option>
                    {['925', '950', '999'].map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </Field>
                <Field label="Para Birimi">
                  <select className="ai-input" {...bind('currency')}>
                    <option value="">Tümü</option>
                    <option value="TRY">TRY (₺)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </Field>
                <Field label="Fiyatlama Türü">
                  <select className="ai-input" {...bind('pricingType')}>
                    <option value="">Tümü</option>
                    <option value="formula">Formüllü</option>
                    <option value="fixed">Sabit Fiyat</option>
                  </select>
                </Field>
                <Field label="Shopify Durumu">
                  <select className="ai-input" {...bind('shopifyStatus')}>
                    <option value="">Tümü</option>
                    <option value="active">Yayında</option>
                    <option value="draft">Taslak</option>
                    <option value="archived">Arşivli</option>
                  </select>
                </Field>
              </div>
            </div>

            <div className="ai-section">
              <h4>Durum</h4>
              <div className="ai-grid">
                <YesNo label="Fotoğraf" value={filters.hasImage} onChange={set('hasImage')} yes="Fotoğrafı var" no="Fotoğrafı yok" />
                <YesNo label="E-Ticaret" value={filters.ecommerceActive} onChange={set('ecommerceActive')} yes="Açık" no="Kapalı" />
                <YesNo label="Shopify" value={filters.shopifyActive} onChange={set('shopifyActive')} yes="Açık" no="Kapalı" />
                <YesNo label="Trendyol" value={filters.trendyolActive} onChange={set('trendyolActive')} yes="Açık" no="Kapalı" />
                <YesNo label="Hepsiburada" value={filters.hepsiburadaActive} onChange={set('hepsiburadaActive')} yes="Açık" no="Kapalı" />
                <YesNo label="Amazon" value={filters.amazonActive} onChange={set('amazonActive')} yes="Açık" no="Kapalı" />
                <YesNo label="N11" value={filters.n11Active} onChange={set('n11Active')} yes="Açık" no="Kapalı" />
                <YesNo label="Set Ürün" value={filters.isSet} onChange={set('isSet')} yes="Set ürünler" no="Tekli ürünler" />
                <YesNo label="Favori" value={filters.isFavoriteFlag} onChange={set('isFavoriteFlag')} yes="Favori" no="Favori değil" />
                <YesNo label="Eski Etiket" value={filters.eskiEtiket} onChange={set('eskiEtiket')} yes="Eski etiketli" no="Yeni etiketli" />
                <YesNo label="İndirim Durumu" value={filters.noDiscount} onChange={set('noDiscount')} yes="İndirimsiz" no="İndirim uygulanabilir" />
                <YesNo label="Özel Tanımlı" value={filters.isSpecialDefined} onChange={set('isSpecialDefined')} yes="Özel tanımlı" no="Normal" />
              </div>
            </div>
          </div>
        )}
      </div>

      {error && <div className="cr-error">Hata: {error}</div>}
      {truncated && <div className="cr-warn">Sonuç çok büyük; ilk 60.000 ürün gösteriliyor. Filtreleri daraltın.</div>}

      {/* ÖZET KARTLAR */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon"><Package size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">{loading ? '…' : fmtInt(totals.count)}</div>
            <div className="stat-label">Ürün · {loading ? '…' : fmtInt(totals.stock)} adet stok</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Scale size={20} style={{ color: '#b45309' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{loading ? '…' : fmtGram(totals.gram)}</div>
            <div className="stat-label">Bekleyen Stok Gramı</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Ban size={20} style={{ color: '#dc2626' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{loading ? '…' : fmtInt(totals.never)}</div>
            <div className="stat-label">Hiç Satılmamış Ürün</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Clock size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{loading ? '…' : `${fmtInt(totals.avgDays)} gün`}</div>
            <div className="stat-label">Ort. Satılmama Süresi</div>
          </div>
        </div>
      </div>

      {/* YAŞ ARALIĞI KARTLARI */}
      <div className="ai-buckets">
        {bucketData.map(b => (
          <button
            key={b.key}
            type="button"
            className={`ai-bucket ${activeBucket === b.key ? 'active' : ''}`}
            style={{ '--ai-c': b.color, '--ai-bg': b.bg }}
            onClick={() => { setActiveBucket(activeBucket === b.key ? null : b.key); setGroupFocus(null); setPage(1); }}
            disabled={!b.count && activeBucket !== b.key}
          >
            <div className="ai-bucket-head">
              <span className="ai-bucket-label">{b.label}</span>
              <span className="ai-bucket-count">{fmtInt(b.count)}</span>
            </div>
            <div className="ai-bucket-sub">{fmtInt(b.stock)} adet{b.gram ? ` • ${fmtGram(b.gram)}` : ''}</div>
            <div className="ai-bucket-bar"><div style={{ width: `${totals.count ? (b.count / totals.count) * 100 : 0}%` }} /></div>
          </button>
        ))}
      </div>

      {/* GRUP ÖZETİ */}
      <div className="data-card">
        <div className="cr-card-head">
          <h3>Özet {activeBucket && <span className="cr-pair-tag cr-focus-tag">{BUCKETS.find(b => b.key === activeBucket)?.label}<X size={12} onClick={() => setActiveBucket(null)} /></span>}</h3>
          <div className="cr-chips">
            {GROUP_OPTIONS.map(g => (
              <button key={g.id} type="button" className={`cr-chip ${groupBy === g.id ? 'active' : ''}`}
                onClick={() => { setGroupBy(g.id); setGroupFocus(null); setPage(1); if (g.id === 'bucket') setGroupSort({ field: 'key', dir: 'asc' }); }}>{g.name}</button>
            ))}
          </div>
        </div>
        {groupBy !== 'none' && (
          <div className="table-wrapper cr-group-table">
            <table className="data-table">
              <thead>
                <tr>
                  {[['key', groupLabel], ['count', 'Ürün'], ['stock', 'Stok Adedi'], ['gram', 'Stok Gramı'], ['never', 'Hiç Satılmamış'], ['avgDays', 'Ort. Gün'], ['share', 'Stok Payı']].map(([f, l]) => (
                    <th key={f} className={`cr-th-sort ${f !== 'key' ? 'cr-num' : ''}`} onClick={() => toggleGroupSort(f)}>
                      {l} <SortIcon active={groupSort.field === f} dir={groupSort.dir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!loading && groups.length === 0 && (
                  <tr><td colSpan={7} className="cr-empty-cell">Bu filtrelerle ürün bulunamadı</td></tr>
                )}
                {groups.map(g => (
                  <tr key={g.key} className={`cr-group-row ${groupFocus === g.key ? 'focused' : ''}`}
                    onClick={() => { setGroupFocus(groupFocus === g.key ? null : g.key); setPage(1); }}
                    title="Detay tablosunu bu gruba daralt">
                    <td><strong>{g.key}</strong></td>
                    <td className="cr-num">{fmtInt(g.count)}</td>
                    <td className="cr-num"><strong>{fmtInt(g.stock)}</strong></td>
                    <td className="cr-num">{g.gram ? fmtGram(g.gram) : '-'}</td>
                    <td className="cr-num">{fmtInt(g.never)}</td>
                    <td className="cr-num">{fmtInt(g.avgDays)}</td>
                    <td className="cr-num">
                      <div className="cr-share"><div className="cr-share-bar" style={{ width: `${Math.max(0, Math.min(100, g.share))}%` }} /><span>%{fmtNum(g.share)}</span></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* DETAY TABLOSU */}
      <div className="data-card">
        <div className="cr-card-head">
          <h3>
            Ürünler <span className="cr-muted">({fmtInt(detailRows.length)} ürün · {fmtInt(detailTotals.stock)} adet)</span>
            {groupFocus != null && (
              <span className="cr-pair-tag cr-focus-tag">{groupFocus}<X size={12} onClick={() => setGroupFocus(null)} /></span>
            )}
          </h3>
          <div className="cr-toolbar">
            <div className="cr-cols" ref={colsRef}>
              <button className="cr-btn" onClick={() => setColsOpen(o => !o)}><Columns3 size={15} /> Sütunlar ({activeColumns.length})</button>
              {colsOpen && (
                <div className="cr-cols-pop">
                  <div className="cr-ms-actions">
                    <button type="button" onClick={() => setVisible(Object.fromEntries(COLUMNS.map(c => [c.key, true])))}>Tümü</button>
                    <button type="button" onClick={() => setVisible({})}>Varsayılan</button>
                  </div>
                  {COLUMNS.map(c => (
                    <label key={c.key} className="cr-ms-item">
                      <input type="checkbox" checked={isVisible(c)} onChange={() => setVisible(v => ({ ...v, [c.key]: !isVisible(c) }))} />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <button className="cr-btn cr-btn-excel" onClick={exportExcel} disabled={loading || !detailRows.length}><FileSpreadsheet size={15} /> Excel</button>
            <button className="cr-btn cr-btn-pdf" onClick={exportPdf} disabled={loading || !detailRows.length}><FileText size={15} /> PDF</button>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="data-table cr-detail-table">
            <thead>
              <tr>
                {activeColumns.map(c => (
                  <th key={c.key} className={`${c.noSort ? '' : 'cr-th-sort'} ${c.num ? 'cr-num' : ''}`} onClick={c.noSort ? undefined : () => toggleSort(c.key)}>
                    {c.label} {!c.noSort && <SortIcon active={sort.field === c.key} dir={sort.dir} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={activeColumns.length || 1} className="cr-empty-cell">Stok verileri analiz ediliyor…</td></tr>
              )}
              {!loading && pageRows.length === 0 && (
                <tr><td colSpan={activeColumns.length || 1} className="cr-empty-cell">Bu filtrelerle ürün bulunamadı</td></tr>
              )}
              {!loading && pageRows.map(r => (
                <tr key={r.id}>
                  {activeColumns.map(c => {
                    if (c.key === 'image') {
                      return (
                        <td key={c.key} className="cr-img-cell">
                          {r.image ? (
                            <img src={`${API_HOST}${r.image}`} alt="" loading="lazy"
                              onClick={() => setImagePreview({ src: `${API_HOST}${r.image}`, name: r.name })} />
                          ) : <span className="cr-no-img">—</span>}
                        </td>
                      );
                    }
                    if (c.key === 'bucket') {
                      const b = bucketOf(r.days_since_sale);
                      return <td key={c.key}><span className="days-badge" style={{ background: b.bg, color: b.color }}>{b.label}</span></td>;
                    }
                    if (c.key === 'last_sale_date' && !r.last_sale_date) {
                      return <td key={c.key}><span className="ai-never">Hiç satılmadı</span></td>;
                    }
                    if (c.key === 'name') return <td key={c.key}><strong>{r.name || '-'}</strong></td>;
                    const v = c.fmt ? c.fmt(r) : (c.get(r) || '-');
                    return <td key={c.key} className={c.num ? 'cr-num' : ''}>{v}</td>;
                  })}
                </tr>
              ))}
            </tbody>
            {!loading && detailRows.length > 0 && (
              <tfoot>
                <tr className="cr-total-row">
                  {activeColumns.map((c, i) => (
                    <td key={c.key} className={c.num ? 'cr-num' : ''}>
                      {i === 0 ? 'TOPLAM'
                        : c.key.startsWith('store_') ? fmtInt(detailRows.reduce((s, r) => s + (c.get(r) || 0), 0))
                          : totalCell(c.key, detailTotals)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {pageCount > 1 && (
          <div className="cr-pager">
            <button className="cr-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Önceki</button>
            <span>Sayfa {page} / {pageCount}</span>
            <button className="cr-btn" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}>Sonraki ›</button>
          </div>
        )}
      </div>

      {imagePreview && (
        <div className="cr-img-modal" onClick={() => setImagePreview(null)}>
          <div className="cr-img-modal-inner" onClick={e => e.stopPropagation()}>
            <button className="cr-img-close" onClick={() => setImagePreview(null)}><X size={18} /></button>
            <img src={imagePreview.src} alt={imagePreview.name} />
            <div>{imagePreview.name}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgingInventory;
